import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import { trackEvent, identifyUser, resetUser } from '@/lib/analytics';
import { registerAndroidPush } from '@/lib/native-push';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signUp: (email: string, password: string, username?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let settled = false;   // an authoritative auth result has arrived
    let retried = false;   // getSession has been retried once
    let graceTimeout: ReturnType<typeof setTimeout> | undefined;

    // Apply an authoritative result and stop loading. `settled` is a closure-local
    // (not the isLoading STATE) so the timeout below reflects live progress — the
    // old code's `if (isLoading)` guard was a stale closure that never gated.
    const settle = (nextSession: Session | null) => {
      if (!isMounted) return;
      settled = true;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      Sentry.setUser(nextSession?.user ? { id: nextSession.user.id, email: nextSession.user.email } : null);
      setIsLoading(false);
    };

    // Any auth event is authoritative — INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED,
    // and crucially the SIGNED_OUT a genuinely-failed refresh emits (a real logout,
    // not a timeout guess).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      settle(s);
      // SAB-538: Android push registration once a session exists (no-op off Android).
      // Mirrors iOS's launch-time registration; fires on login and app-open. Idempotent.
      if (s?.user) void registerAndroidPush();
    });

    const checkSession = () => {
      supabase.auth.getSession()
        .then(({ data: { session } }) => {
          if (!settled) settle(session);
          if (session?.user) void registerAndroidPush();
        })
        .catch(() => {
          if (!isMounted || settled) return;
          if (!retried) { retried = true; checkSession(); }   // single retry on error
          else settle(null);
        });
    };
    checkSession();

    // Safety timeout. Do NOT show login while a refresh may still be in flight
    // (the old 3s->login caused the logout/login flicker AND real premature logouts
    // on slow mobile). On timeout we retry getSession ONCE and grant a short grace
    // window; only if it still hasn't settled do we surface the logged-out UI.
    // Native gets a longer budget: cold WKWebView + cellular + a token-refresh
    // round-trip regularly exceeds 3s. We never `await` a possibly-hanging
    // getSession here — the retry settles via its own promise, the grace timer
    // is the hard fallback.
    const PRIMARY_MS = Capacitor.isNativePlatform() ? 8000 : 3000;
    const GRACE_MS = 4000;
    const timeout = setTimeout(() => {
      if (!isMounted || settled) return;
      if (!retried) { retried = true; checkSession(); }
      graceTimeout = setTimeout(() => {
        if (isMounted && !settled) {
          console.warn('[Auth] Session check timed out after retry — showing login');
          setIsLoading(false);   // give up: render logged-out (session stays null)
        }
      }, GRACE_MS);
    }, PRIMARY_MS);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
      if (graceTimeout) clearTimeout(graceTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signUp = useCallback(async (email: string, password: string, username?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/welcome`,
        data: {
          full_name: username || email.split('@')[0],
        },
      },
    });
    if (!error) {
      trackEvent('signup', { method: 'email' });
    }
    return { error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (s?.user) {
        identifyUser(s.user.id, s.user.email || '', s.user.user_metadata?.full_name || '');
        Sentry.setUser({ id: s.user.id, email: s.user.email });
        trackEvent('login', { method: 'email' });
      }
    }
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    trackEvent('logout');
    resetUser();
    Sentry.setUser(null);
    await supabase.auth.signOut();
    window.location.href = '/auth';
  }, []);

  const value = useMemo(() => ({
    user, session, isLoading, signUp, signIn, signOut,
  }), [user, session, isLoading, signUp, signIn, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
